import { useState, useEffect, useRef, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Droplets } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import MapStyleSwitcher from "@/components/MapStyleSwitcher";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const NWI_TILE_URL =
  "https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0";

function calcAcres(polygon) {
  if (!polygon?.coordinates?.[0]) return 0;
  const coords = polygon.coordinates[0];
  if (coords.length < 3) return 0;
  let total = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const xi = coords[i][0] * Math.cos((coords[i][1] * Math.PI) / 180) * 111320;
    const yi = coords[i][1] * 110540;
    const xj = coords[j][0] * Math.cos((coords[j][1] * Math.PI) / 180) * 111320;
    const yj = coords[j][1] * 110540;
    total += xi * yj - xj * yi;
  }
  return (Math.abs(total) / 2) * 0.000247105;
}

function addAreaLabel(map, poly, index) {
  if (!poly?.coordinates?.[0]) return;
  const coords = poly.coordinates[0];
  const lon = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const el = document.createElement("div");
  const inner = document.createElement("div");
  inner.style.cssText =
    "background:#f97316;color:white;padding:4px 10px;border-radius:20px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.3)";
  inner.textContent = `Area ${index + 1}: ${calcAcres(poly).toFixed(2)} ac`;
  el.className = "co-area-label";
  el.appendChild(inner);
  new mapboxgl.Marker({ element: el }).setLngLat([lon, lat]).addTo(map);
}

/**
 * Self-contained read-only Mapbox display for the Change Order customer view.
 * Shows original quote polygons (blue) and change order areas (orange).
 */
export function COViewMapCard({ changeOrder, quote }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const showWetlandsRef = useRef(true);
  const [showWetlands, setShowWetlands] = useState(true);

  const reloadViewMapLayers = useCallback(() => {
    const m = mapInstanceRef.current;
    if (!m || !changeOrder?.map_areas?.length) return;

    if (!m.getSource("nwi-wetlands")) {
      m.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
      m.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      if (!showWetlandsRef.current) m.setLayoutProperty("nwi-wetlands-layer", "visibility", "none");
    }

    if (quote?.polygons?.length > 0 && !m.getSource("quote-areas")) {
      const qf = quote.polygons.map((poly, i) => ({ type: "Feature", geometry: poly, properties: { label: `Original Area ${i + 1}` } }));
      m.addSource("quote-areas", { type: "geojson", data: { type: "FeatureCollection", features: qf } });
      m.addLayer({ id: "quote-areas-fill", type: "fill", source: "quote-areas", paint: { "fill-color": "#3b82f6", "fill-opacity": 0.15 } });
      m.addLayer({ id: "quote-areas-line", type: "line", source: "quote-areas", paint: { "line-color": "#3b82f6", "line-width": 2, "line-dasharray": [3, 2] } });
    }

    if (!m.getSource("co-areas")) {
      const cof = changeOrder.map_areas.map((poly, i) => ({ type: "Feature", geometry: poly, properties: { label: `CO Area ${i + 1}` } }));
      m.addSource("co-areas", { type: "geojson", data: { type: "FeatureCollection", features: cof } });
      m.addLayer({ id: "co-areas-fill", type: "fill", source: "co-areas", paint: { "fill-color": "#f97316", "fill-opacity": 0.3 } });
      m.addLayer({ id: "co-areas-line", type: "line", source: "co-areas", paint: { "line-color": "#f97316", "line-width": 3 } });
    }

    changeOrder.map_areas.forEach((poly, i) => addAreaLabel(m, poly, i));

    if (quote?.property_address && !m.getSource("parcels")) {
      axios.post(`${API}/parcels/by-address`, { address: quote.property_address })
        .then((resp) => {
          if (resp.data.features?.length > 0 && mapInstanceRef.current && !mapInstanceRef.current.getSource("parcels")) {
            mapInstanceRef.current.addSource("parcels", { type: "geojson", data: { type: "FeatureCollection", features: resp.data.features } });
            mapInstanceRef.current.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 } });
            mapInstanceRef.current.addLayer({ id: "parcels-line", type: "line", source: "parcels", paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 } });
          }
        })
        .catch(() => {});
    }
  }, [changeOrder, quote]);

  useEffect(() => {
    if (!changeOrder?.map_areas?.length || !MAPBOX_TOKEN || mapInstanceRef.current) return;
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const allCoords = [];
    changeOrder.map_areas.forEach((poly) => {
      if (poly?.coordinates?.[0]) poly.coordinates[0].forEach((c) => allCoords.push(c));
    });
    if (quote?.polygons) {
      quote.polygons.forEach((poly) => {
        if (poly?.coordinates?.[0]) poly.coordinates[0].forEach((c) => allCoords.push(c));
      });
    }

    let center = [-78.6382, 35.7796];
    if (allCoords.length > 0) {
      const lons = allCoords.map((c) => c[0]);
      const lats = allCoords.map((c) => c[1]);
      center = [(Math.min(...lons) + Math.max(...lons)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center,
      zoom: 16,
      interactive: true,
      scrollZoom: true,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      map.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
      map.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });

      if (quote?.polygons?.length > 0) {
        const quoteFeatures = quote.polygons.map((poly, i) => ({
          type: "Feature", geometry: poly, properties: { label: `Original Area ${i + 1}` },
        }));
        map.addSource("quote-areas", { type: "geojson", data: { type: "FeatureCollection", features: quoteFeatures } });
        map.addLayer({ id: "quote-areas-fill", type: "fill", source: "quote-areas", paint: { "fill-color": "#3b82f6", "fill-opacity": 0.15 } });
        map.addLayer({ id: "quote-areas-line", type: "line", source: "quote-areas", paint: { "line-color": "#3b82f6", "line-width": 2, "line-dasharray": [3, 2] } });
      }

      const coFeatures = changeOrder.map_areas.map((poly, i) => ({
        type: "Feature", geometry: poly, properties: { label: `CO Area ${i + 1}` },
      }));
      map.addSource("co-areas", { type: "geojson", data: { type: "FeatureCollection", features: coFeatures } });
      map.addLayer({ id: "co-areas-fill", type: "fill", source: "co-areas", paint: { "fill-color": "#f97316", "fill-opacity": 0.3 } });
      map.addLayer({ id: "co-areas-line", type: "line", source: "co-areas", paint: { "line-color": "#f97316", "line-width": 3 } });

      changeOrder.map_areas.forEach((poly, i) => addAreaLabel(map, poly, i));

      if (allCoords.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        allCoords.forEach((c) => bounds.extend(c));
        map.fitBounds(bounds, { padding: 60 });
      }

      if (quote?.property_address) {
        axios.post(`${API}/parcels/by-address`, { address: quote.property_address })
          .then((resp) => {
            if (resp.data.features?.length > 0 && !map.getSource("parcels")) {
              map.addSource("parcels", { type: "geojson", data: { type: "FeatureCollection", features: resp.data.features } });
              map.addLayer({ id: "parcels-fill", type: "fill", source: "parcels", paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 } });
              map.addLayer({ id: "parcels-line", type: "line", source: "parcels", paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 } });
            }
          })
          .catch(() => {});
      }
    });

    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [changeOrder, quote]);

  const handleWetlandsToggle = () => {
    if (!mapInstanceRef.current?.getLayer("nwi-wetlands-layer")) return;
    const next = !showWetlandsRef.current;
    mapInstanceRef.current.setLayoutProperty("nwi-wetlands-layer", "visibility", next ? "visible" : "none");
    showWetlandsRef.current = next;
    setShowWetlands(next);
  };

  return (
    <div>
      <h3 className="font-semibold text-zinc-900 mb-3">Change Order Areas</h3>
      <div className="border rounded-lg overflow-hidden">
        <div className="relative">
          <div ref={mapContainerRef} className="h-[400px] w-full" data-testid="co-view-map" />
          <button
            onClick={handleWetlandsToggle}
            className={`absolute bottom-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg transition-colors ${showWetlands ? "bg-cyan-600 text-white" : "bg-white text-zinc-700 border"}`}
          >
            <Droplets className="w-3.5 h-3.5" /> Wetlands
          </button>
          <div className="absolute bottom-3 right-3 z-10">
            <MapStyleSwitcher mapRef={mapInstanceRef} onStyleLoad={reloadViewMapLayers} />
          </div>
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 z-10 text-xs">
            {quote?.polygons?.length > 0 && (
              <div className="mb-2">
                <p className="font-semibold text-blue-600 uppercase tracking-wider mb-0.5">Original Quote</p>
                {quote.polygons.map((poly, i) => (
                  <div key={`orig-area-${i}`} className="flex items-center justify-between gap-4 text-blue-600">
                    <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-blue-500 inline-block" /> Area {i + 1}</span>
                    <span className="font-medium">{calcAcres(poly).toFixed(2)} ac</span>
                  </div>
                ))}
              </div>
            )}
            <p className="font-semibold text-orange-600 uppercase tracking-wider mb-0.5">Change Order</p>
            {changeOrder.map_areas.map((poly, i) => (
              <div key={`co-area-${i}`} className="flex items-center justify-between gap-4 text-orange-600">
                <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-orange-500 inline-block" /> Area {i + 1}</span>
                <span className="font-medium">{calcAcres(poly).toFixed(2)} ac</span>
              </div>
            ))}
            {changeOrder.total_map_acres > 0 && (
              <div className="border-t border-zinc-200 mt-1.5 pt-1.5 flex justify-between font-bold text-orange-700">
                <span>Total</span>
                <span>{changeOrder.total_map_acres.toFixed(2)} ac</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
